import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import API_URL from '../config/api';
import { getNames, getCode } from 'country-list';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast, { Toaster } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

// Stripe Publishable Key
const stripePromise = loadStripe('pk_test_51SbnhkCTQQgXusTBAmiVAvhZsk0CrbQSEXMy8c5WUPT9nMc1gBbZgg0nVbFhgVKqDGG81iajBE8IW62gmk2zYWmq00bB5ZyLxL');

// دالة خارجية للحصول على التوكن
const getAuthToken = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }
  return token;
};

// إعدادات التوست
const toastSuccess = (message) => {
  toast.success(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#000',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#000',
      secondary: '#fff',
    },
  });
};

// دالة لعرض توست خطأ
const toastError = (message) => {
  toast.error(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#dc2626',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#dc2626',
      secondary: '#fff',
    },
  });
};

// Modal component memoized لمنع إعادة تصيير غير ضرورية
const Modal = memo(({ isOpen, onClose, children, size = 'max-w-2xl', title = '' }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      // تأخير إخفاء المودال حتى تنتهي animation
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!shouldRender) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white border border-gray-100 md:p-10 p-6 ${size} w-full max-h-[90vh] overflow-y-auto rounded-lg md:rounded-none transition-transform duration-300 ${
          isOpen ? 'transform translate-y-0' : 'transform translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
});

// Stripe Card Form Component
const StripeCardForm = ({ 
  onSubmit, 
  isLoading, 
  makePrimary = false,
  editingPayment = null 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!cardholderName.trim()) {
      setCardError('Cardholder name is required');
      return;
    }

    setIsProcessing(true);
    setCardError('');

    try {
      const cardElement = elements.getElement(CardElement);
      
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName,
        },
      });

      if (error) {
        setCardError(error.message);
        setIsProcessing(false);
        return;
      }

      // استدعاء دالة onSubmit التي تأتي من الـ parent
      await onSubmit({
        paymentMethodId: paymentMethod.id,
        makePrimary,
        cardholderName,
        brand: paymentMethod.card.brand,
        last4: paymentMethod.card.last4,
        expMonth: paymentMethod.card.exp_month,
        expYear: paymentMethod.card.exp_year
      });

    } catch (error) {
      console.error('Error creating payment method:', error);
      setCardError('Failed to create payment method. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#000',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        '::placeholder': {
          color: '#a0aec0',
        },
      },
      invalid: {
        color: '#dc2626',
      },
    },
    hidePostalCode: true,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">
          Cardholder Name
        </label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal"
          required
        />
      </div>

      <div>
        <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">
          Card Details
        </label>
        <div className="border border-gray-200 p-3 rounded">
          <CardElement options={cardElementOptions} />
        </div>
        {cardError && (
          <p className="text-red-500 text-xs mt-2">{cardError}</p>
        )}
      </div>

      {!editingPayment && (
        <div>
          <label className="flex items-center space-x-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={makePrimary}
              onChange={() => {}}
              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
              readOnly
            />
            <span className="text-sm font-normal">Set as primary payment method</span>
          </label>
        </div>
      )}

      {cardError && (
        <div className="bg-red-50 border border-red-100 p-4">
          <div className="flex items-start space-x-3">
            <i className="fa-solid fa-exclamation-circle text-red-500 mt-0.5"></i>
            <div>
              <p className="text-sm font-medium text-red-700">Error</p>
              <p className="text-xs text-red-600 mt-1 font-normal">{cardError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-end space-y-3 md:space-y-0 md:space-x-4 pt-6">
        <button
          type="submit"
          disabled={!stripe || isProcessing || isLoading}
          className={`text-xs px-6 py-3 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide w-full md:w-auto ${
            (!stripe || isProcessing || isLoading) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isProcessing ? 'Processing...' : (editingPayment ? 'Update Card' : 'Add Payment Method')}
        </button>
      </div>
    </form>
  );
};

// قائمة Primary Collecting Interest الثابتة
const COLLECTING_INTERESTS = [
  'Contemporary Art',
  'Modern Art',
  'Photography',
  'Sculpture',
  'Mixed Media',
];

// دالة للحصول على قائمة الدول مع رموز الهاتف الحقيقية
const getCountriesWithPhoneCodes = () => {
  // قائمة كاملة برموز الهاتف الحقيقية للدول
  const countryPhoneCodes = {
    'AF': '+93', 'AL': '+355', 'DZ': '+213', 'AS': '+1684', 'AD': '+376', 'AO': '+244',
    'AI': '+1264', 'AQ': '+672', 'AG': '+1268', 'AR': '+54', 'AM': '+374', 'AW': '+297',
    'AU': '+61', 'AT': '+43', 'AZ': '+994', 'BS': '+1242', 'BH': '+973', 'BD': '+880',
    'BB': '+1246', 'BY': '+375', 'BE': '+32', 'BZ': '+501', 'BJ': '+229', 'BM': '+1441',
    'BT': '+975', 'BO': '+591', 'BA': '+387', 'BW': '+267', 'BR': '+55', 'IO': '+246',
    'VG': '+1284', 'BN': '+673', 'BG': '+359', 'BF': '+226', 'BI': '+257', 'KH': '+855',
    'CM': '+237', 'CA': '+1', 'CV': '+238', 'KY': '+1345', 'CF': '+236', 'TD': '+235',
    'CL': '+56', 'CN': '+86', 'CX': '+61', 'CC': '+61', 'CO': '+57', 'KM': '+269',
    'CK': '+682', 'CR': '+506', 'HR': '+385', 'CU': '+53', 'CW': '+599', 'CY': '+357',
    'CZ': '+420', 'CD': '+243', 'DK': '+45', 'DJ': '+253', 'DM': '+1767', 'DO': '+1809',
    'TL': '+670', 'EC': '+593', 'EG': '+20', 'SV': '+503', 'GQ': '+240', 'ER': '+291',
    'EE': '+372', 'ET': '+251', 'FK': '+500', 'FO': '+298', 'FJ': '+679', 'FI': '+358',
    'FR': '+33', 'PF': '+689', 'GA': '+241', 'GM': '+220', 'GE': '+995', 'DE': '+49',
    'GH': '+233', 'GI': '+350', 'GR': '+30', 'GL': '+299', 'GD': '+1473', 'GU': '+1671',
    'GT': '+502', 'GG': '+44', 'GN': '+224', 'GW': '+245', 'GY': '+592', 'HT': '+509',
    'HN': '+504', 'HK': '+852', 'HU': '+36', 'IS': '+354', 'IN': '+91', 'ID': '+62',
    'IR': '+98', 'IQ': '+964', 'IE': '+353', 'IM': '+44', 'IL': '+972', 'IT': '+39',
    'CI': '+225', 'JM': '+1876', 'JP': '+81', 'JE': '+44', 'JO': '+962', 'KZ': '+7',
    'KE': '+254', 'KI': '+686', 'XK': '+383', 'KW': '+965', 'KG': '+996', 'LA': '+856',
    'LV': '+371', 'LB': '+961', 'LS': '+266', 'LR': '+231', 'LY': '+218', 'LI': '+423',
    'LT': '+370', 'LU': '+352', 'MO': '+853', 'MK': '+389', 'MG': '+261', 'MW': '+265',
    'MY': '+60', 'MV': '+960', 'ML': '+223', 'MT': '+356', 'MH': '+692', 'MR': '+222',
    'MU': '+230', 'YT': '+262', 'MX': '+52', 'FM': '+691', 'MD': '+373', 'MC': '+377',
    'MN': '+976', 'ME': '+382', 'MS': '+1664', 'MA': '+212', 'MZ': '+258', 'MM': '+95',
    'NA': '+264', 'NR': '+674', 'NP': '+977', 'NL': '+31', 'NC': '+687', 'NZ': '+64',
    'NI': '+505', 'NE': '+227', 'NG': '+234', 'NU': '+683', 'KP': '+850', 'MP': '+1670',
    'NO': '+47', 'OM': '+968', 'PK': '+92', 'PW': '+680', 'PS': '+970', 'PA': '+507',
    'PG': '+675', 'PY': '+595', 'PE': '+51', 'PH': '+63', 'PN': '+64', 'PL': '+48',
    'PT': '+351', 'PR': '+1787', 'QA': '+974', 'CG': '+242', 'RE': '+262', 'RO': '+40',
    'RU': '+7', 'RW': '+250', 'BL': '+590', 'SH': '+290', 'KN': '+1869', 'LC': '+1758',
    'MF': '+590', 'PM': '+508', 'VC': '+1784', 'WS': '+685', 'SM': '+378', 'ST': '+239',
    'SA': '+966', 'SN': '+221', 'RS': '+381', 'SC': '+248', 'SL': '+232', 'SG': '+65',
    'SX': '+1721', 'SK': '+421', 'SI': '+386', 'SB': '+677', 'SO': '+252', 'ZA': '+27',
    'KR': '+82', 'SS': '+211', 'ES': '+34', 'LK': '+94', 'SD': '+249', 'SR': '+597',
    'SJ': '+47', 'SZ': '+268', 'SE': '+46', 'CH': '+41', 'SY': '+963', 'TW': '+886',
    'TJ': '+992', 'TZ': '+255', 'TH': '+66', 'TG': '+228', 'TK': '+690', 'TO': '+676',
    'TT': '+1868', 'TN': '+216', 'TR': '+90', 'TM': '+993', 'TC': '+1649', 'TV': '+688',
    'VI': '+1340', 'UG': '+256', 'UA': '+380', 'AE': '+971', 'GB': '+44', 'US': '+1',
    'UY': '+598', 'UZ': '+998', 'VU': '+678', 'VA': '+379', 'VE': '+58', 'VN': '+84',
    'WF': '+681', 'EH': '+212', 'YE': '+967', 'ZM': '+260', 'ZW': '+263'
  };

  const countryNames = getNames();
  const countriesWithCodes = [];

  countryNames.forEach(countryName => {
    const countryCode = getCode(countryName);
    if (countryCode && countryPhoneCodes[countryCode]) {
      countriesWithCodes.push({
        name: countryName,
        code: countryCode,
        phoneCode: countryPhoneCodes[countryCode]
      });
    }
  });

  // إذا لم يكن هناك رمز هاتف للدولة، نضيف رمز افتراضي
  countryNames.forEach(countryName => {
    const countryCode = getCode(countryName);
    if (countryCode && !countryPhoneCodes[countryCode]) {
      countriesWithCodes.push({
        name: countryName,
        code: countryCode,
        phoneCode: '+1' // رمز افتراضي
      });
    }
  });

  return countriesWithCodes.sort((a, b) => a.name.localeCompare(b.name));
};

// Edit Profile Modal Component
const EditProfileModal = ({ 
  isOpen, 
  onClose, 
  userProfile, 
  onSubmit, 
  isLoading,
  initialValues,
  validationSchema,
  phoneCodes
}) => {
  const [initialized, setInitialized] = useState(false);
  
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setStatus, resetForm }) => {
      const result = await onSubmit(values);
      setStatus(result);
      
      if (result.type === 'success') {
        setTimeout(() => {
          onClose();
          setStatus(null);
          resetForm();
        }, 2000);
      }
    }
  });

  useEffect(() => {
    if (isOpen && !initialized) {
      formik.setValues(initialValues);
      formik.setStatus(null);
      setInitialized(true);
    }
    
    if (!isOpen && initialized) {
      setInitialized(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    formik.setStatus(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="flex justify-between items-center mb-6 md:mb-8">
        <h3 className="text-base font-medium mb-1.5">Edit Profile Information</h3>
        <button 
          className="text-gray-400 hover:text-black p-1 transition-colors"
          onClick={handleClose}
        >
          <i className="fa-solid fa-times text-sm"></i>
        </button>
      </div>
      
      <form className="space-y-7" onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">First Name</label>
            <input 
              type="text" 
              name="firstname"
              value={formik.values.firstname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
                formik.touched.firstname && formik.errors.firstname
                  ? 'border-red-500'
                  : 'border-gray-200'
              }`}
            />
            {formik.touched.firstname && formik.errors.firstname && (
              <div className="text-red-500 text-xs mt-1">{formik.errors.firstname}</div>
            )}
          </div>
          
          <div>
            <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Last Name</label>
            <input 
              type="text" 
              name="lastname"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Email Address</label>
          <input 
            type="email" 
            defaultValue={userProfile.email}
            className="w-full border border-gray-200 px-4 py-3 text-sm bg-gray-50 focus:outline-none focus:border-gray-300 transition-colors font-normal"
            readOnly
          />
          <p className="text-xs text-gray-600 mt-3 font-normal">Email cannot be changed</p>
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Phone Number</label>
          <div className="flex space-x-2">
            <div className="w-1/4">
              <select 
                name="phoneCode"
                value={formik.values.phoneCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal"
              >
                <option value="">Select Code</option>
                {phoneCodes.map((country, index) => (
                  <option key={index} value={country.phoneCode}>
                    {country.phoneCode} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <input 
                type="tel" 
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Phone number"
                className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal"
              />
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-3 font-normal">
            Full phone number: {formik.values.phoneCode} {formik.values.phoneNumber}
          </p>
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">City</label>
          <input 
            type="text" 
            name="city"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="City"
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.city && formik.errors.city
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          />
          {formik.touched.city && formik.errors.city && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.city}</div>
          )}
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Primary Collecting Interest</label>
          <select 
            name="primaryCollectingInterest"
            value={formik.values.primaryCollectingInterest}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.primaryCollectingInterest && formik.errors.primaryCollectingInterest
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          >
            <option value="">Select collecting interest</option>
            {COLLECTING_INTERESTS.map((interest, index) => (
              <option key={index} value={interest}>{interest}</option>
            ))}
          </select>
          {formik.touched.primaryCollectingInterest && formik.errors.primaryCollectingInterest && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.primaryCollectingInterest}</div>
          )}
        </div>

        {formik.status && formik.status.type === 'error' && (
          <div className="bg-red-50 border border-red-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-exclamation-circle text-red-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-red-700">Error</p>
                <p className="text-xs text-red-600 mt-1 font-normal">{formik.status.message}</p>
              </div>
            </div>
          </div>
        )}

        {formik.status && formik.status.type === 'success' && (
          <div className="bg-green-50 border border-green-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-check-circle text-green-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-green-700">Success</p>
                <p className="text-xs text-green-600 mt-1 font-normal">{formik.status.message}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row justify-end space-y-3 md:space-y-0 md:space-x-4 pt-8 border-t border-gray-100">
          <button 
            type="button" 
            className="text-xs px-6 py-3 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className={`text-xs px-6 py-3 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide w-full md:w-auto ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Add Address Modal Component
const AddAddressModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading,
  editingAddress,
  addresses,
  countries,
  phoneCodes
}) => {
  const [initialized, setInitialized] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const formik = useFormik({
    initialValues: {
      fullName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      country: 'Denmark',
      phoneCode: '+45',
      phoneNumber: '',
      isBilling: false,
      isShipping: false,
      isPrimary: false
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Full name is required'),
      addressLine1: Yup.string().required('Address line 1 is required'),
      city: Yup.string().required('City is required'),
      postalCode: Yup.string().required('Postal code is required'),
      country: Yup.string().required('Country is required'),
      phoneCode: Yup.string(),
      phoneNumber: Yup.string().test(
        'phone-number-required',
        'Phone number is required',
        function(value) {
          // إذا تم اختيار كود هاتف، يجب إدخال رقم الهاتف
          const { phoneCode } = this.parent;
          if (phoneCode && phoneCode.trim() !== '') {
            return value && value.trim() !== '';
          }
          return true;
        }
      )
    }),
    enableReinitialize: false,
    onSubmit: async (values) => {
      setSuccessMessage('');
      setErrorMessage('');
      
      const result = await onSubmit(values, editingAddress);
      
      if (result.type === 'success') {
        setSuccessMessage(result.message);
        setTimeout(() => {
          handleClose();
          setSuccessMessage('');
          setErrorMessage('');
        }, 2000);
      } else {
        setErrorMessage(result.message);
      }
    }
  });

  useEffect(() => {
    if (isOpen && !initialized) {
      if (editingAddress) {
        const address = addresses.find(addr => addr.id === editingAddress);
        if (address) {
          const phoneParts = address.phoneNumber ? address.phoneNumber.split(' ') : ['+45', ''];
          formik.setValues({
            fullName: address.fullName,
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2 || '',
            city: address.city,
            postalCode: address.postalCode,
            country: address.country,
            phoneCode: phoneParts[0] || '+45',
            phoneNumber: phoneParts.slice(1).join(' ') || '',
            isBilling: address.isBilling,
            isShipping: address.isShipping,
            isPrimary: address.isPrimary
          });
        }
      } else {
        formik.resetForm();
        // تعيين دولة افتراضية بناءً على البلدان المتاحة
        const defaultCountry = countries.length > 0 ? countries[0] : 'Denmark';
        formik.setFieldValue('country', defaultCountry);
        // تعيين كود هاتف افتراضي
        const defaultPhoneCode = phoneCodes.find(country => country.code === 'DK')?.phoneCode || '+45';
        formik.setFieldValue('phoneCode', defaultPhoneCode);
      }
      setSuccessMessage('');
      setErrorMessage('');
      setInitialized(true);
    }
    
    if (!isOpen && initialized) {
      setInitialized(false);
      setSuccessMessage('');
      setErrorMessage('');
    }
  }, [isOpen, editingAddress]);

  const handleClose = () => {
    setSuccessMessage('');
    setErrorMessage('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="flex justify-between items-center mb-6 md:mb-8">
        <h3 className="text-base font-medium mb-1.5">
          {editingAddress ? 'Edit Address' : 'Add New Address'}
        </h3>
        <button 
          className="text-gray-400 hover:text-black p-1 transition-colors"
          onClick={handleClose}
        >
          <i className="fa-solid fa-times text-sm"></i>
        </button>
      </div>
      
      <form className="space-y-7" onSubmit={formik.handleSubmit}>
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Address Type</label>
          <div className="flex flex-wrap gap-3 md:gap-6">
            <label className="flex items-center space-x-2.5 cursor-pointer">
              <input 
                type="checkbox" 
                name="isBilling"
                checked={formik.values.isBilling}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0" 
              />
              <span className="text-sm font-normal">Billing</span>
            </label>
            <label className="flex items-center space-x-2.5 cursor-pointer">
              <input 
                type="checkbox" 
                name="isShipping"
                checked={formik.values.isShipping}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0" 
              />
              <span className="text-sm font-normal">Shipping</span>
            </label>
            {!editingAddress && (
              <label className="flex items-center space-x-2.5 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="isPrimary"
                  checked={formik.values.isPrimary}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0" 
                />
                <span className="text-sm font-normal">Set as Primary</span>
              </label>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Full Name or Company</label>
          <input 
            type="text" 
            name="fullName"
            value={formik.values.fullName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter name" 
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.fullName && formik.errors.fullName
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          />
          {formik.touched.fullName && formik.errors.fullName && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.fullName}</div>
          )}
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Address Line 1</label>
          <input 
            type="text" 
            name="addressLine1"
            value={formik.values.addressLine1}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Street address" 
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.addressLine1 && formik.errors.addressLine1
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          />
          {formik.touched.addressLine1 && formik.errors.addressLine1 && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.addressLine1}</div>
          )}
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Address Line 2 (Optional)</label>
          <input 
            type="text" 
            name="addressLine2"
            value={formik.values.addressLine2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Apartment, suite, etc." 
            className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Postal Code</label>
            <input 
              type="text" 
              name="postalCode"
              value={formik.values.postalCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="1234" 
              className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
                formik.touched.postalCode && formik.errors.postalCode
                  ? 'border-red-500'
                  : 'border-gray-200'
              }`}
            />
            {formik.touched.postalCode && formik.errors.postalCode && (
              <div className="text-red-500 text-xs mt-1">{formik.errors.postalCode}</div>
            )}
          </div>
          
          <div>
            <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">City</label>
            <input 
              type="text" 
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="City" 
              className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
                formik.touched.city && formik.errors.city
                  ? 'border-red-500'
                  : 'border-gray-200'
            }`}
            />
            {formik.touched.city && formik.errors.city && (
              <div className="text-red-500 text-xs mt-1">{formik.errors.city}</div>
            )}
          </div>
          
          <div>
            <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Country</label>
            <select 
              name="country"
              value={formik.values.country}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
                formik.touched.country && formik.errors.country
                  ? 'border-red-500'
                  : 'border-gray-200'
              }`}
            >
              {countries.map((country, index) => (
                <option key={index} value={country}>
                  {country}
                </option>
              ))}
            </select>
            {formik.touched.country && formik.errors.country && (
              <div className="text-red-500 text-xs mt-1">{formik.errors.country}</div>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Phone Number</label>
          <div className="flex space-x-2">
            <div className="w-1/3">
              <select 
                name="phoneCode"
                value={formik.values.phoneCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full border border-gray-200 px-3 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal"
              >
                <option value="">Select Code</option>
                {phoneCodes.map((country, index) => (
                  <option key={index} value={country.phoneCode}>
                    {country.phoneCode} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <input 
                type="tel" 
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Phone number" 
                className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? 'border-red-500'
                    : 'border-gray-200'
                }`}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.phoneNumber}</div>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-3 font-normal">
            Full phone number: {formik.values.phoneCode} {formik.values.phoneNumber}
          </p>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-exclamation-circle text-red-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-red-700">Error</p>
                <p className="text-xs text-red-600 mt-1 font-normal">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-check-circle text-green-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-green-700">Success</p>
                <p className="text-xs text-green-600 mt-1 font-normal">{successMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row justify-end space-y-3 md:space-y-0 md:space-x-4 pt-8 border-t border-gray-100">
          <button 
            type="button" 
            className="text-xs px-6 py-3 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={isLoading}
            className={`text-xs px-6 py-3 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide w-full md:w-auto ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? 'Saving...' : (editingAddress ? 'Update Address' : 'Save Address')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [initialized, setInitialized] = useState(false);
  
  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required('Current password is required'),
      newPassword: Yup.string()
        .min(8, 'New password must be at least 8 characters long')
        .required('New password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'New passwords do not match')
        .required('Confirm password is required')
    }),
    enableReinitialize: false,
    onSubmit: async (values, { setStatus, resetForm }) => {
      const result = await onSubmit(values);
      setStatus(result);
      
      if (result.type === 'success') {
        setTimeout(() => {
          onClose();
          setStatus(null);
          resetForm();
        }, 2000);
      }
    }
  });

  useEffect(() => {
    if (isOpen && !initialized) {
      formik.resetForm();
      formik.setStatus(null);
      setInitialized(true);
    }
    
    if (!isOpen && initialized) {
      setInitialized(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    formik.setStatus(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="max-w-md">
      <div className="flex justify-between items-center mb-6 md:mb-8">
        <h3 className="text-base font-medium mb-1.5">Change Password</h3>
        <button 
          className="text-gray-400 hover:text-black p-1 transition-colors"
          onClick={handleClose}
        >
          <i className="fa-solid fa-times text-sm"></i>
        </button>
      </div>
      
      <form className="space-y-7" onSubmit={formik.handleSubmit}>
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Current Password</label>
          <input 
            type="password" 
            name="currentPassword"
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter current password"
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.currentPassword && formik.errors.currentPassword
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          />
          {formik.touched.currentPassword && formik.errors.currentPassword && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.currentPassword}</div>
            )}
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">New Password</label>
          <input 
            type="password" 
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter new password"
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.newPassword && formik.errors.newPassword
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          />
          {formik.touched.newPassword && formik.errors.newPassword && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.newPassword}</div>
          )}
          <p className="text-xs text-gray-600 mt-3 font-normal">Minimum 8 characters with letters and numbers</p>
        </div>
        
        <div>
          <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Confirm New Password</label>
          <input 
            type="password" 
            name="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Confirm new password"
            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
              formik.touched.confirmPassword && formik.errors.confirmPassword
                ? 'border-red-500'
                : 'border-gray-200'
            }`}
          />
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className="text-red-500 text-xs mt-1">{formik.errors.confirmPassword}</div>
          )}
        </div>

        {formik.status && formik.status.type === 'error' && (
          <div className="bg-red-50 border border-red-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-exclamation-circle text-red-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-red-700">Error</p>
                <p className="text-xs text-red-600 mt-1 font-normal">{formik.status.message}</p>
              </div>
            </div>
          </div>
        )}

        {formik.status && formik.status.type === 'success' && (
          <div className="bg-green-50 border border-green-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-check-circle text-green-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-green-700">Success</p>
                <p className="text-xs text-green-600 mt-1 font-normal">{formik.status.message}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row justify-end space-y-3 md:space-y-0 md:space-x-4 pt-8 border-t border-gray-100">
          <button 
            type="button" 
            className="text-xs px-6 py-3 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className={`text-xs px-6 py-3 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide w-full md:w-auto ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Changing...' : 'Update Password'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Add Payment Modal Component
const AddPaymentModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading,
  editingPayment,
  makePrimary = false 
}) => {
  const [initialized, setInitialized] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  useEffect(() => {
    if (isOpen && !initialized) {
      setSuccessMessage('');
      setErrorMessage('');
      setInitialized(true);
    }
    
    if (!isOpen && initialized) {
      setInitialized(false);
      setSuccessMessage('');
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setSuccessMessage('');
    setErrorMessage('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="max-w-md">
      <div className="flex justify-between items-center mb-6 md:mb-8">
        <h3 className="text-base font-medium mb-1.5">
          {editingPayment ? 'Update Payment Method' : 'Add Payment Method'}
        </h3>
        <button 
          className="text-gray-400 hover:text-black p-1 transition-colors"
          onClick={handleClose}
        >
          <i className="fa-solid fa-times text-sm"></i>
        </button>
      </div>
      
      <Elements stripe={stripePromise}>
        <StripeCardForm 
          onSubmit={async (paymentData) => {
            setSuccessMessage('');
            setErrorMessage('');
            
            const result = await onSubmit(paymentData, editingPayment);
            
            if (result.type === 'success') {
              setSuccessMessage(result.message);
              setTimeout(() => {
                handleClose();
                setSuccessMessage('');
                setErrorMessage('');
              }, 2000);
            } else {
              setErrorMessage(result.message);
            }
          }}
          isLoading={isLoading}
          makePrimary={makePrimary}
          editingPayment={editingPayment}
        />
      </Elements>

      {errorMessage && (
        <div className="bg-red-50 border border-red-100 p-4 mt-4">
          <div className="flex items-start space-x-3">
            <i className="fa-solid fa-exclamation-circle text-red-500 mt-0.5"></i>
            <div>
              <p className="text-sm font-medium text-red-700">Error</p>
              <p className="text-xs text-red-600 mt-1 font-normal">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-100 p-4 mt-4">
          <div className="flex items-start space-x-3">
            <i className="fa-solid fa-check-circle text-green-500 mt-0.5"></i>
            <div>
              <p className="text-sm font-medium text-green-700">Success</p>
              <p className="text-xs text-green-600 mt-1 font-normal">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

// Main Settings Component
const Settings = () => {
  // State لتخزين تفضيلات الإشعارات
  const [notifications, setNotifications] = useState({
    offers_orders: {
      new_offer_received: { email: true, in_app: true },
      offer_status_updates: { email: true, in_app: true },
      shipping_updates: { email: true, in_app: false }
    },
    payments_invoices: {
      payment_confirmations: { email: true, in_app: true },
      invoice_reminders: { email: true, in_app: true }
    },
    following_artists: {
      new_artwork_available: { email: true, in_app: true },
      price_changes: { email: false, in_app: true }
    }
  });

  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showDeleteAddressConfirm, setShowDeleteAddressConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [showDeletePaymentConfirm, setShowDeletePaymentConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [countries, setCountries] = useState([]);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [setPrimaryStatus, setSetPrimaryStatus] = useState({ type: '', message: '' });

  // User profile state
  const [userProfile, setUserProfile] = useState({
    firstname: '',
    lastname: '',
    email: '',
    city: '',
    country: '',
    primaryCollectingInterest: '',
    createdAt: '',
    phoneNumber: ''
  });

  // Initial edit profile values
  const editProfileInitialValues = useMemo(() => {
    const phoneParts = userProfile.phoneNumber ? userProfile.phoneNumber.split(' ') : ['+45', ''];
    return {
      firstname: userProfile.firstname || '',
      lastname: userProfile.lastname || '',
      city: userProfile.city || '',
      primaryCollectingInterest: userProfile.primaryCollectingInterest || '',
      phoneCode: phoneParts[0] || '+45',
      phoneNumber: phoneParts.slice(1).join(' ') || '',
      fullPhoneNumber: userProfile.phoneNumber || ''
    };
  }, [userProfile]);

  // Memoized handlers using useCallback
  const handleNotificationChange = useCallback((category, type, channel) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [type]: {
          ...prev[category][type],
          [channel]: !prev[category][type][channel]
        }
      }
    }));
  }, []);

  // دالة لتحويل البيانات من API إلى format الـ state
  const mapApiToState = useCallback((apiData) => {
    if (!apiData) return null;
    
    return {
      offers_orders: {
        new_offer_received: { 
          email: apiData.offers_orders?.new_offer_received?.email === 1,
          in_app: apiData.offers_orders?.new_offer_received?.in_app === 1
        },
        offer_status_updates: { 
          email: apiData.offers_orders?.offer_status_updates?.email === 1,
          in_app: apiData.offers_orders?.offer_status_updates?.in_app === 1
        },
        shipping_updates: { 
          email: apiData.offers_orders?.shipping_updates?.email === 1,
          in_app: apiData.offers_orders?.shipping_updates?.in_app === 1
        }
      },
      payments_invoices: {
        payment_confirmations: { 
          email: apiData.payments_invoices?.payment_confirmations?.email === 1,
          in_app: apiData.payments_invoices?.payment_confirmations?.in_app === 1
        },
        invoice_reminders: { 
          email: apiData.payments_invoices?.invoice_reminders?.email === 1,
          in_app: apiData.payments_invoices?.invoice_reminders?.in_app === 1
        }
      },
      following_artists: {
        new_artwork_available: { 
          email: apiData.following_artists?.new_artwork_available?.email === 1,
          in_app: apiData.following_artists?.new_artwork_available?.in_app === 1
        },
        price_changes: { 
          email: apiData.following_artists?.price_changes?.email === 1,
          in_app: apiData.following_artists?.price_changes?.in_app === 1
        }
      }
    };
  }, []);

  // دالة لتحويل البيانات من state إلى format الـ API
  const mapStateToApi = useCallback((stateData) => {
    return {
      offers_orders: {
        new_offer_received: { 
          email: stateData.offers_orders.new_offer_received.email ? 1 : 0,
          in_app: stateData.offers_orders.new_offer_received.in_app ? 1 : 0
        },
        offer_status_updates: { 
          email: stateData.offers_orders.offer_status_updates.email ? 1 : 0,
          in_app: stateData.offers_orders.offer_status_updates.in_app ? 1 : 0
        },
        shipping_updates: { 
          email: stateData.offers_orders.shipping_updates.email ? 1 : 0,
          in_app: stateData.offers_orders.shipping_updates.in_app ? 1 : 0
        }
      },
      payments_invoices: {
        payment_confirmations: { 
          email: stateData.payments_invoices.payment_confirmations.email ? 1 : 0,
          in_app: stateData.payments_invoices.payment_confirmations.in_app ? 1 : 0
        },
        invoice_reminders: { 
          email: stateData.payments_invoices.invoice_reminders.email ? 1 : 0,
          in_app: stateData.payments_invoices.invoice_reminders.in_app ? 1 : 0
        }
      },
      following_artists: {
        new_artwork_available: { 
          email: stateData.following_artists.new_artwork_available.email ? 1 : 0,
          in_app: stateData.following_artists.new_artwork_available.in_app ? 1 : 0
        },
        price_changes: { 
          email: stateData.following_artists.price_changes.email ? 1 : 0,
          in_app: stateData.following_artists.price_changes.in_app ? 1 : 0
        }
      }
    };
  }, []);

  // دالة لتحميل تفضيلات الإشعارات من API
  const loadNotificationPreferences = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/notification-preferences`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load notification preferences');
      }

      const data = await response.json();
      
      if (data.data && data.data.preferences) {
        const mappedData = mapApiToState(data.data.preferences);
        if (mappedData) {
          setNotifications(mappedData);
        }
      } else if (data.preferences) {
        const mappedData = mapApiToState(data.preferences);
        if (mappedData) {
          setNotifications(mappedData);
        }
      }
      
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      toastError('Failed to load notification preferences');
    } finally {
      setLoadingNotifications(false);
    }
  }, [mapApiToState]);

  // دالة لحفظ تفضيلات الإشعارات إلى API
  const saveNotificationPreferences = useCallback(async () => {
    setSavingNotifications(true);
    try {
      const token = getAuthToken();
      const apiData = mapStateToApi(notifications);
      
      const response = await fetch(`${API_URL}/api/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        throw new Error('Failed to save notification preferences');
      }

      const data = await response.json();
      
      return { success: true, message: data.message };
      
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toastError(error.message || 'Failed to save notification preferences');
      return { success: false, message: error.message };
    } finally {
      setSavingNotifications(false);
    }
  }, [notifications, mapStateToApi]);

  const handleEditAddress = useCallback((addressId) => {
    setEditingAddress(addressId);
    setShowAddAddressModal(true);
  }, []);

  const handleDeleteAddress = useCallback((addressId) => {
    setAddressToDelete(addressId);
    setShowDeleteAddressConfirm(true);
  }, []);

  const confirmDeleteAddress = useCallback(async () => {
    if (addressToDelete) {
      try {
        await deleteAddress(addressToDelete);
        setShowDeleteAddressConfirm(false);
        setAddressToDelete(null);
      } catch (error) {
        toastError(error.message || 'Failed to delete address');
      }
    }
  }, [addressToDelete]);

  const handleEditPayment = useCallback((paymentMethodId) => {
    setEditingPayment(paymentMethodId);
    setShowAddPaymentModal(true);
  }, []);

  const handleDeletePayment = useCallback((paymentMethodId) => {
    setPaymentToDelete(paymentMethodId);
    setShowDeletePaymentConfirm(true);
  }, []);

  const confirmDeletePayment = useCallback(async () => {
    if (paymentToDelete) {
      try {
        await deletePaymentMethod(paymentToDelete);
        setShowDeletePaymentConfirm(false);
        setPaymentToDelete(null);
        toastSuccess('Payment method removed successfully');
      } catch (error) {
        toastError(error.message || 'Failed to remove payment method');
      }
    }
  }, [paymentToDelete]);

  const handleSetPrimaryPayment = useCallback(async (paymentMethodId) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/payment-methods/make-primary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentMethodId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to set payment method as primary');
      }

      await loadPaymentMethods();
      
      setSetPrimaryStatus({ type: 'success', message: data.message || 'Payment method set as primary successfully' });
      
      setTimeout(() => {
        setSetPrimaryStatus({ type: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error setting payment method as primary:', error);
      setSetPrimaryStatus({ type: 'error', message: error.message || 'Failed to set payment method as primary' });
      
      setTimeout(() => {
        setSetPrimaryStatus({ type: '', message: '' });
      }, 3000);
    }
  }, []);

  // Function to handle link opening
  const handleLinkClick = useCallback((e, url) => {
    if (!isMobile) {
      e.preventDefault();
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [isMobile]);

  useEffect(() => {
    // Get all country names using country-list library
    const countryNames = getNames();
    const sortedCountries = countryNames.sort();
    setCountries(sortedCountries);

    // Get phone codes for all countries
    const countryPhoneCodes = getCountriesWithPhoneCodes();
    setPhoneCodes(countryPhoneCodes);

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Load user profile, addresses, payment methods, and notification preferences
    loadUserProfile();
    loadAddresses();
    loadPaymentMethods();
    loadNotificationPreferences();
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load user profile from API
  const loadUserProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load user profile');
      }

      const data = await response.json();
      
      if (data.success && data.data && data.data.user) {
        const user = data.data.user;
        
        const createdAt = new Date(user.createdAt);
        const formattedDate = createdAt.toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        });

        const userPhone = user.phoneNumber || '';

        setUserProfile({
          firstname: user.firstname || '',
          lastname: user.lastname || '',
          email: user.email || '',
          city: user.city || '',
          country: user.country || '',
          primaryCollectingInterest: user.primaryCollectingInterest || '',
          createdAt: formattedDate,
          phoneNumber: userPhone
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      toastError('Failed to load profile information');
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  // Edit Profile submission handler
  const handleEditProfileSubmit = useCallback(async (values) => {
    setEditingProfile(true);
    
    try {
      const token = getAuthToken();
      
      const updateData = {
        firstname: values.firstname,
        city: values.city,
        primaryCollectingInterest: values.primaryCollectingInterest
      };

      if (values.lastname && values.lastname.trim() !== '') {
        updateData.lastname = values.lastname;
      }

      const fullPhone = values.phoneCode && values.phoneNumber 
        ? `${values.phoneCode} ${values.phoneNumber}`
        : '';
      
      if (fullPhone.trim() !== '') {
        updateData.phoneNumber = fullPhone;
      }

      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      await loadUserProfile();
      
      toastSuccess(data.message || 'Profile updated successfully');
      
      return { type: 'success', message: data.message || 'Profile updated successfully!' };
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toastError(error.message || 'Failed to update profile');
      return { type: 'error', message: error.message || 'Failed to update profile. Please try again.' };
    } finally {
      setEditingProfile(false);
    }
  }, [loadUserProfile]);

  // Address submission handler
  const handleAddressSubmit = useCallback(async (values, editingAddress) => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      
      const addressData = {
        fullName: values.fullName,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        postalCode: values.postalCode,
        country: values.country,
        phoneNumber: `${values.phoneCode} ${values.phoneNumber}`,
        isBilling: values.isBilling,
        isShipping: values.isShipping,
        isPrimary: values.isPrimary
      };

      const url = editingAddress 
        ? `${API_URL}/api/addresses/${editingAddress}`
        : `${API_URL}/api/addresses`;
      
      const method = editingAddress ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addressData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${editingAddress ? 'update' : 'add'} address`);
      }

      await loadAddresses();
      
      toastSuccess(data.message || (editingAddress ? 'Address updated successfully' : 'Address added successfully'));
      
      return { 
        type: 'success', 
        message: data.message || (editingAddress ? 'Address updated successfully!' : 'Address added successfully!') 
      };

    } catch (error) {
      console.error('Error saving address:', error);
      toastError(error.message || `Failed to ${editingAddress ? 'update' : 'add'} address`);
      return { 
        type: 'error', 
        message: error.message || `Failed to ${editingAddress ? 'update' : 'add'} address. Please try again.` 
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Change Password submission handler
  const handleChangePasswordSubmit = useCallback(async (values) => {
    setChangingPassword(true);

    try {
      const token = getAuthToken();

      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      toastSuccess(data.message || 'Password changed successfully');

      return { type: 'success', message: data.message || 'Password changed successfully!' };

    } catch (error) {
      console.error('Error changing password:', error);
      toastError(error.message || 'Failed to change password');
      return { type: 'error', message: error.message || 'Failed to change password. Please try again.' };
    } finally {
      setChangingPassword(false);
    }
  }, []);

  // Payment submission handler
  const handlePaymentSubmit = useCallback(async (paymentData, editingPayment) => {
    setAddingPayment(true);
    try {
      const token = getAuthToken();
      
      const url = `${API_URL}/api/payment-methods/add`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          paymentMethodId: paymentData.paymentMethodId,
          makePrimary: paymentData.makePrimary,
          cardholderName: paymentData.cardholderName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add payment method');
      }

      await loadPaymentMethods();
      
      toastSuccess(data.message || 'Payment method added successfully');
      
      return { 
        type: 'success', 
        message: data.message || 'Payment method added successfully!' 
      };

    } catch (error) {
      console.error('Error adding payment method:', error);
      toastError(error.message || 'Failed to add payment method');
      return { 
        type: 'error', 
        message: error.message || 'Failed to add payment method. Please try again.' 
      };
    } finally {
      setAddingPayment(false);
    }
  }, []);

  // Load addresses from API
  const loadAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/addresses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load addresses');
      }

      const data = await response.json();
       
      if (data.data && data.data.addresses && Array.isArray(data.data.addresses)) {
        setAddresses(data.data.addresses);
      } else if (data.addresses && Array.isArray(data.addresses)) {
        setAddresses(data.addresses);
      } else if (Array.isArray(data)) {
        setAddresses(data);
      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      setAddresses([]);
      toastError('Failed to load addresses');
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  // Load payment methods from API
  const loadPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load payment methods');
      }

      const data = await response.json();
      
      if (data.success && data.data && data.data.paymentMethods && Array.isArray(data.data.paymentMethods)) {
        // معالجة البيانات القادمة من API
        const processedPaymentMethods = data.data.paymentMethods.map(payment => ({
          id: payment.id,
          type: payment.type,
          created: payment.created,
          customer: payment.customer,
          card: {
            brand: payment.card.brand,
            last4: payment.card.last4,
            expMonth: payment.card.exp_month,
            expYear: payment.card.exp_year,
            funding: payment.card.funding,
            country: payment.card.country
          },
          cardholderName: payment.cardholderName || '',
          // تحديد ما إذا كانت الطريقة الأساسية
          isPrimary: payment.id === data.data.defaultPaymentMethod
        }));
        
        setPaymentMethods(processedPaymentMethods);
      } else {
        setPaymentMethods([]);
      }
      
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setPaymentMethods([]);
      toastError('Failed to load payment methods');
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  // Delete payment method
  const deletePaymentMethod = useCallback(async (paymentMethodId) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/payment-methods/remove`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentMethodId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete payment method');
      }

      await loadPaymentMethods();
      
      return { success: true, message: data.message };

    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    }
  }, [loadPaymentMethods]);

  // Set address as primary
  const setAddressAsPrimary = useCallback(async (addressId) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/addresses/${addressId}/set-primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to set address as primary');
      }

      await loadAddresses();
      
      toastSuccess(data.message || 'Address set as primary successfully');
    } catch (error) {
      console.error('Error setting address as primary:', error);
      toastError(error.message || 'Failed to set address as primary');
    }
  }, [loadAddresses]);

  // Delete address with primary address handling
  const deleteAddress = useCallback(async (addressId) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/addresses/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete address');
      }

      setShowDeleteAddressConfirm(false);
      setAddressToDelete(null);
      
      await loadAddresses();
      
      toastSuccess(data.message || 'Address deleted successfully');
      
      return { success: true, message: data.message };

    } catch (error) {
      console.error('Error deleting address:', error);
      throw error;
    }
  }, [loadAddresses]);

  // Get card icon based on brand
  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'fa-brands fa-cc-visa';
      case 'mastercard':
        return 'fa-brands fa-cc-mastercard';
      case 'amex':
      case 'american express':
        return 'fa-brands fa-cc-amex';
      case 'discover':
        return 'fa-brands fa-cc-discover';
      case 'diners':
        return 'fa-brands fa-cc-diners-club';
      case 'jcb':
        return 'fa-brands fa-cc-jcb';
      default:
        return 'fa-regular fa-credit-card';
    }
  };

  // Format expiration date
  const formatExpirationDate = (expMonth, expYear) => {
    if (!expMonth || !expYear) return 'N/A';
    const month = expMonth.toString().padStart(2, '0');
    const year = expYear.toString().slice(-2);
    return `${month}/${year}`;
  };

  // Delete Account Confirmation Modal
  const DeleteAccountModal = () => {
    const [initialized, setInitialized] = useState(false);
    
    const formik = useFormik({
      initialValues: {
        deletePassword: ''
      },
      validationSchema: Yup.object({
        deletePassword: Yup.string().required('Password is required to confirm account deletion')
      }),
      enableReinitialize: false,
      onSubmit: async (values) => {
        setDeletingAccount(true);
        setDeleteAccountError('');

        try {
          const token = getAuthToken();

          const response = await fetch(`${API_URL}/api/auth/account`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              password: values.deletePassword
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to delete account');
          }

          localStorage.removeItem('authToken');
          sessionStorage.removeItem('authToken');

          window.location.href = '/login';
          
        } catch (error) {
          console.error('Error deleting account:', error);
          setDeleteAccountError(error.message || 'Failed to delete account. Please try again.');
        } finally {
          setDeletingAccount(false);
        }
      }
    });

    useEffect(() => {
      if (showDeleteAccountModal && !initialized) {
        formik.resetForm();
        setDeleteAccountError('');
        setInitialized(true);
      }
      
      if (!showDeleteAccountModal && initialized) {
        setInitialized(false);
      }
    }, [showDeleteAccountModal]);

    const handleClose = () => {
      setDeleteAccountError('');
      setShowDeleteAccountModal(false);
    };

    return (
      <Modal isOpen={showDeleteAccountModal} onClose={handleClose} size="max-w-md">
        <div className="flex justify-between items-center mb-6 md:mb-8">
          <h3 className="text-base font-medium mb-1.5">Delete Account</h3>
          <button 
            className="text-gray-400 hover:text-black p-1 transition-colors"
            onClick={handleClose}
          >
            <i className="fa-solid fa-times text-sm"></i>
          </button>
        </div>
        
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-100 p-4">
            <div className="flex items-start space-x-3">
              <i className="fa-solid fa-exclamation-triangle text-red-500 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-red-700">This action cannot be undone</p>
                <p className="text-xs text-red-600 mt-1 font-normal">
                  All your data including profile information, order history, saved items, and preferences will be permanently deleted.
                </p>
              </div>
            </div>
          </div>
          
          <form className="space-y-6" onSubmit={formik.handleSubmit}>
            <div>
              <label className="block text-[10px] md:text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">
                Enter your password to confirm
              </label>
              <input 
                type="password" 
                name="deletePassword"
                value={formik.values.deletePassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Current password"
                className={`w-full border px-4 py-3 text-sm focus:outline-none focus:border-gray-300 transition-colors font-normal ${
                  formik.touched.deletePassword && formik.errors.deletePassword
                    ? 'border-red-500'
                    : 'border-gray-200'
                }`}
              />
              {formik.touched.deletePassword && formik.errors.deletePassword && (
                <p className="text-xs text-red-600 mt-2 font-normal">{formik.errors.deletePassword}</p>
              )}
              {deleteAccountError && (
                <p className="text-xs text-red-600 mt-2 font-normal">{deleteAccountError}</p>
              )}
              <p className="text-xs text-gray-600 mt-3 font-normal">
                Type your current password to confirm account deletion
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row justify-end space-y-3 md:space-y-0 md:space-x-4 pt-8 border-t border-gray-100">
              <button 
                type="button" 
                className="text-xs px-6 py-3 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
                onClick={handleClose}
                disabled={deletingAccount}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className={`text-xs px-6 py-3 bg-red-600 text-white hover:bg-red-700 transition-all duration-200 font-normal tracking-wide w-full md:w-auto ${deletingAccount ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Delete Account Permanently'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    );
  };

  // Delete Address Confirmation Modal
  const DeleteAddressConfirmModal = () => {
    const handleClose = () => {
      setShowDeleteAddressConfirm(false);
      setAddressToDelete(null);
    };

    return (
      <Modal isOpen={showDeleteAddressConfirm} onClose={handleClose} size="max-w-md">
        <div className="text-center py-6 md:py-8">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
            <i className="fa-solid fa-trash text-white text-lg md:text-xl"></i>
          </div>
          
          <h3 className="text-base font-medium mb-2">Delete Address?</h3>
          <p className="text-sm text-gray-600 font-normal mb-6 md:mb-8">
            Are you sure you want to delete this address? This action cannot be undone.
          </p>
          
          <div className="flex flex-col md:flex-row justify-center space-y-3 md:space-y-0 md:space-x-4">
            <button 
              type="button" 
              className="text-xs px-6 py-3 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="text-xs px-6 py-3 bg-black text-white transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
              onClick={confirmDeleteAddress}
            >
              Delete Address
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Delete Payment Confirmation Modal
  const DeletePaymentConfirmModal = () => {
    const handleClose = () => {
      setShowDeletePaymentConfirm(false);
      setPaymentToDelete(null);
    };

    return (
      <Modal isOpen={showDeletePaymentConfirm} onClose={handleClose} size="max-w-md">
        <div className="text-center py-6 md:py-8">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
            <i className="fa-solid fa-credit-card text-red-500 text-lg md:text-xl"></i>
          </div>
          
          <h3 className="text-base font-medium mb-2">Remove Payment Method?</h3>
          <p className="text-sm text-gray-600 font-normal mb-6 md:mb-8">
            Are you sure you want to remove this payment method? You'll need to re-enter the details if you want to use it again.
          </p>
          
          <div className="flex flex-col md:flex-row justify-center space-y-3 md:space-y-0 md:space-x-4">
            <button 
              type="button" 
              className="text-xs px-6 py-3 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="text-xs px-6 py-3 bg-red-600 text-white hover:bg-red-700 transition-all duration-200 font-normal tracking-wide w-full md:w-auto"
              onClick={confirmDeletePayment}
            >
              Remove Payment
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Toaster Component */}
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#fff',
            color: '#000',
            border: '1px solid #e5e5e5',
            padding: '16px',
            borderRadius: '0px',
            fontSize: '14px',
            fontWeight: 'normal',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#000',
              secondary: '#fff',
            },
          },
          error: {
            style: {
              background: '#fff',
              color: '#dc2626',
            },
            iconTheme: {
              primary: '#dc2626',
              secondary: '#fff',
            },
          },
        }}
      />
      
      <section className="hidden md:block px-10 py-8 border-b border-gray-100">
        <div>
          <h1 className="text-sm font-medium tracking-[0.2em] uppercase text-gallery-dark mb-2">
            Account & Settings
          </h1>
          <p className="text-sm text-gray-600 font-normal">Manage profile, billing addresses, and notification preferences</p>
        </div>
      </section>
      
      {/* Profile Information */}
      <section className="px-4 md:px-10 py-6 md:py-10">
        <div className="max-w-4xl">
          {/* Desktop View */}
          <div className="hidden md:block border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-medium mb-1.5">Profile Information</h2>
                <p className="text-sm text-gray-600 font-normal">Personal details and contact information</p>
              </div>
              <button 
                className="text-xs px-5 py-2.5 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
                onClick={() => setShowEditProfileModal(true)}
              >
                <i className="fa-solid fa-pen text-[10px] mr-2"></i>Edit
              </button>
            </div>
            {loadingProfile ? (
              <div className="px-8 py-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mb-4"></div>
                <p className="text-sm text-gray-600">Loading profile information...</p>
              </div>
            ) : (
              <div className="px-8 py-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                  <div>
                    <label className="block text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Full Name</label>
                    <p className="text-sm text-gallery-dark font-normal">
                      {userProfile.firstname} {userProfile.lastname}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Email Address</label>
                    <p className="text-sm text-gallery-dark font-normal">{userProfile.email}</p>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Phone Number</label>
                    <p className="text-sm text-gallery-dark font-normal">
                      {userProfile.phoneNumber || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Member Since</label>
                    <p className="text-sm text-gallery-dark font-normal">{userProfile.createdAt}</p>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">City</label>
                    <p className="text-sm text-gallery-dark font-normal">
                      {userProfile.city || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 uppercase tracking-[0.1em] mb-3 font-medium">Primary Collecting Interest</label>
                    <p className="text-sm text-gallery-dark font-normal">
                      {userProfile.primaryCollectingInterest || 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-xs text-gray-500 uppercase tracking-wider">Profile Information</h3>
              <button 
                className="text-xs font-medium flex items-center"
                onClick={() => setShowEditProfileModal(true)}
              >
                <i className="fa-solid fa-pen text-[10px] mr-1.5"></i>Edit
              </button>
            </div>
            {loadingProfile ? (
              <div className="py-8 text-center bg-white border border-gray-100 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900 mb-3"></div>
                <p className="text-sm text-gray-600">Loading profile...</p>
              </div>
            ) : (
              <div className="bg-white border border-gray-100 rounded-lg divide-y divide-gray-100">
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Full Name</p>
                  <p className="text-sm font-medium">
                    {userProfile.firstname} {userProfile.lastname}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Email Address</p>
                  <p className="text-sm font-medium">{userProfile.email}</p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Phone Number</p>
                  <p className="text-sm font-medium">
                    {userProfile.phoneNumber || 'Not provided'}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">City</p>
                  <p className="text-sm font-medium">
                    {userProfile.city || 'Not provided'}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Primary Collecting Interest</p>
                  <p className="text-sm font-medium">
                    {userProfile.primaryCollectingInterest || 'Not specified'}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">Member Since</p>
                  <p className="text-sm font-medium">{userProfile.createdAt}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Security Settings */}
      <section className="px-4 md:px-10 py-6 md:py-10 border-t border-gray-100">
        <div className="max-w-4xl">
          {/* Desktop View */}
          <div className="hidden md:block border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-base font-medium mb-1.5">Security & Privacy</h2>
              <p className="text-sm text-gray-600 font-normal">Password and privacy settings</p>
            </div>
            <div className="px-8 py-8 space-y-8">
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium mb-1.5">Password</h3>
                    <p className="text-xs text-gray-600 font-normal">You can update your Password here</p>
                  </div>
                  <button 
                    className="text-xs px-5 py-2.5 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
                    onClick={() => setShowChangePasswordModal(true)}
                  >
                    Change Password
                  </button>
                </div>
              </div>
              <div className="pt-8 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium mb-1.5 text-red-600">Delete Account</h3>
                    <p className="text-xs text-gray-600 font-normal">Permanently delete account and all associated data</p>
                  </div>
                  <button 
                    className="text-xs px-5 py-2.5 text-red-600 hover:text-red-700 transition-colors font-normal tracking-wide"
                    onClick={() => setShowDeleteAccountModal(true)}
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-1">Security & Privacy</h3>
            <div className="bg-white border border-gray-100 rounded-lg divide-y divide-gray-100">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Password</p>
                  <p className="text-xs text-gray-600 mt-0.5">Last changed 3 months ago</p>
                </div>
                <button 
                  className="text-xs px-3 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  onClick={() => setShowChangePasswordModal(true)}
                >
                  Change
                </button>
              </div>
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Delete Account</p>
                  <p className="text-xs text-gray-600 mt-0.5">Permanently delete your account</p>
                </div>
                <button 
                  className="text-xs px-3 py-2 text-red-600"
                  onClick={() => setShowDeleteAccountModal(true)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Billing Addresses */}
      <section className="px-4 md:px-10 py-6 md:py-10 border-t border-gray-100">
        <div className="max-w-4xl">
          {/* Desktop View */}
          <div className="hidden md:block border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-medium mb-1.5">Billing Addresses</h2>
                <p className="text-sm text-gray-600 font-normal">Billing and shipping addresses</p>
              </div>
              <button 
                className="text-xs px-5 py-2.5 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide"
                onClick={() => {
                  setEditingAddress(null);
                  setShowAddAddressModal(true);
                }}
              >
                <span><i className="fa-solid fa-plus text-[10px] mr-2"></i></span>Add Address
              </button>
            </div>

            {loadingAddresses ? (
              <div key="loading" className="px-8 py-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mb-4"></div>
                <p className="text-sm text-gray-600">Loading addresses...</p>
              </div>
            ) : addresses.length === 0 ? (
              <div key="no-address" className="px-8 py-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span><i className="fa-solid fa-map-marker-alt text-gray-400 text-xl"></i></span>
                </div>
                <h3 className="text-sm font-medium mb-2">No addresses yet</h3>
                <p className="text-sm text-gray-600 mb-6">You haven't added any billing or shipping addresses.</p>
                <button 
                  className="text-xs px-5 py-2.5 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
                  onClick={() => setShowAddAddressModal(true)}
                >
                  Add Your First Address
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {addresses.map((address) => (
                  <div key={address.id} className="px-8 py-8">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2.5 mb-4">
                          {address.isPrimary && (
                            <span className="inline-block px-2.5 py-1 bg-black text-white text-[10px] uppercase tracking-[0.1em] font-medium">Primary</span>
                          )}
                          {address.isBilling && (
                            <span className="inline-block px-2.5 py-1 bg-green-50 text-green-700 text-[10px] uppercase tracking-[0.1em] font-medium">Billing</span>
                          )}
                          {address.isShipping && (
                            <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 text-[10px] uppercase tracking-[0.1em] font-medium">Shipping</span>
                          )}
                        </div>
                        <div className="space-y-1.5 text-sm font-normal">
                          <p className="font-medium">{address.fullName}</p>
                          <p className="text-gray-700">{address.addressLine1}</p>
                          {address.addressLine2 && <p className="text-gray-700">{address.addressLine2}</p>}
                          <p className="text-gray-700">{address.postalCode} {address.city}</p>
                          <p className="text-gray-700">{address.country}</p>
                          <p className="text-gray-700 mt-3">{address.phoneNumber}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          className="text-xs px-4 py-2 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
                          onClick={() => handleEditAddress(address.id)}
                        >
                          Edit
                        </button>
                        {!address.isPrimary && (
                          <button 
                            className="text-xs px-4 py-2 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
                            onClick={() => setAddressAsPrimary(address.id)}
                          >
                            Set Primary
                          </button>
                        )}
                        <button 
                          className="text-xs px-4 py-2 text-red-600 hover:text-red-700 transition-colors font-normal tracking-wide"
                          onClick={() => handleDeleteAddress(address.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-xs text-gray-500 uppercase tracking-wider">Billing Addresses</h3>
              <button 
                className="text-xs font-medium"
                onClick={() => {
                  setEditingAddress(null);
                  setShowAddAddressModal(true);
                }}
              >
                <span><i className="fa-solid fa-plus mr-1"></i></span>Add
              </button>
            </div>

            {loadingAddresses ? (
              <div key="loading-mobile" className="py-8 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900 mb-3"></div>
                <p className="text-sm text-gray-600">Loading addresses...</p>
              </div>
            ) : addresses.length === 0 ? (
              <div key="no-address-mobile" className="py-8 text-center bg-white border border-gray-100 rounded-lg">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span><i className="fa-solid fa-map-marker-alt text-gray-400"></i></span>
                </div>
                <p className="text-sm text-gray-600 mb-4">No addresses saved yet</p>
                <button 
                  className="text-xs px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  onClick={() => setShowAddAddressModal(true)}
                >
                  Add First Address
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map((address) => (
                  <div key={address.id} className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex flex-wrap gap-2">
                        {address.isPrimary && (
                          <span className="inline-block px-2 py-1 bg-black text-white text-xs uppercase tracking-[0.1em] rounded">Primary</span>
                        )}
                        {address.isBilling && (
                          <span className="inline-block px-2 py-1 bg-green-50 text-green-700 text-xs uppercase tracking-[0.1em] rounded">Billing</span>
                        )}
                        {address.isShipping && (
                          <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs uppercase tracking-[0.1em] rounded">Shipping</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400" onClick={() => handleEditAddress(address.id)}>
                          <span><i className="fa-solid fa-pen text-xs"></i></span>
                        </button>
                        {!address.isPrimary && (
                          <button className="text-blue-400" onClick={() => setAddressAsPrimary(address.id)}>
                            <span><i className="fa-solid fa-star text-xs"></i></span>
                          </button>
                        )}
                        <button className="text-red-400" onClick={() => handleDeleteAddress(address.id)}>
                          <span><i className="fa-solid fa-trash text-xs"></i></span>
                        </button>
                      </div>
                    </div>
                    <div className="space-y-0.5 text-sm">
                      <p className="font-medium">{address.fullName}</p>
                      <p className="text-gray-600">{address.addressLine1}</p>
                      {address.addressLine2 && <p className="text-gray-600">{address.addressLine2}</p>}
                      <p className="text-gray-600">{address.postalCode} {address.city}</p>
                      <p className="text-gray-600">{address.country}</p>
                      <p className="text-gray-600 mt-2">{address.phoneNumber}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
     {/* Payment Methods */}
<section className="px-4 md:px-10 py-6 md:py-10 border-t border-gray-100">
  <div className="max-w-4xl">
    {/* Desktop View */}
    <div className="hidden md:block border border-gray-100">
      <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium mb-1.5">Payment Methods</h2>
          <p className="text-sm text-gray-600 font-normal">Manage cards and payment options</p>
        </div>
        <button 
          className="text-xs px-5 py-2.5 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide"
          onClick={() => {
            setEditingPayment(null);
            setShowAddPaymentModal(true);
          }}
        >
          <span><i className="fa-solid fa-plus text-[10px] mr-2"></i></span>Add Payment Method
        </button>
      </div>

      {setPrimaryStatus.type && (
        <div key="status" className={`px-8 py-4 ${setPrimaryStatus.type === 'success' ? 'bg-green-50 border-l-4 border-green-500' : 'bg-red-50 border-l-4 border-red-500'}`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span>
                {setPrimaryStatus.type === 'success' ? (
                  <i className="fa-solid fa-check-circle text-green-500 text-lg"></i>
                ) : (
                  <i className="fa-solid fa-exclamation-circle text-red-500 text-lg"></i>
                )}
              </span>
            </div>
            <div className="ml-3">
              <p className={`text-sm ${setPrimaryStatus.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {setPrimaryStatus.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {loadingPaymentMethods ? (
        <div key="loading" className="px-8 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-sm text-gray-600">Loading payment methods...</p>
        </div>
      ) : paymentMethods.length === 0 ? (
        <div key="empty" className="px-8 py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span><i className="fa-regular fa-credit-card text-gray-400 text-xl"></i></span>
          </div>
          <h3 className="text-sm font-medium mb-2">No payment methods yet</h3>
          <p className="text-sm text-gray-600 mb-6">You haven't added any payment methods.</p>
          <button 
            className="text-xs px-5 py-2.5 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
            onClick={() => {
              setEditingPayment(null);
              setShowAddPaymentModal(true);
            }}
          >
            Add Your First Payment Method
          </button>
        </div>
      ) : (
        <div key="list" className="divide-y divide-gray-100">
          {paymentMethods.map((payment) => (
            <div key={payment.id} className="px-8 py-8">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-5">
                  <div className="w-12 h-12 bg-gallery-gray rounded flex items-center justify-center">
                    <span><i className={`${getCardIcon(payment.card?.brand)} text-2xl text-gray-700`}></i></span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2.5 mb-3">
                      {payment.isPrimary && (
                        <span className="inline-block px-2.5 py-1 bg-black text-white text-[10px] uppercase tracking-[0.1em] font-medium">Primary</span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm font-normal">
                      <p className="font-medium">
                        {payment.card?.brand ? payment.card.brand.charAt(0).toUpperCase() + payment.card.brand.slice(1) : 'Card'} 
                        {payment.card?.last4 && ` ending in ${payment.card.last4}`}
                      </p>
                      <p className="text-gray-600">
                        Expires {formatExpirationDate(payment.card?.expMonth, payment.card?.expYear)}
                      </p>
                      {payment.cardholderName && (
                        <p className="text-gray-600">Cardholder: {payment.cardholderName}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {!payment.isPrimary && (
                    <button 
                      className="text-xs px-4 py-2 border border-gray-300 hover:border-gray-400 hover:bg-gallery-gray transition-all duration-200 font-normal tracking-wide"
                      onClick={() => handleSetPrimaryPayment(payment.id)}
                    >
                      Set Primary
                    </button>
                  )}
                  <button 
                    className="text-xs px-4 py-2 text-red-600 hover:text-red-700 transition-colors font-normal tracking-wide"
                    onClick={() => handleDeletePayment(payment.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Mobile View */}
    <div className="md:hidden">
      {setPrimaryStatus.type && (
        <div key="mobile-status" className={`mb-4 p-4 ${setPrimaryStatus.type === 'success' ? 'bg-green-50 border border-green-200 rounded-lg' : 'bg-red-50 border border-red-200 rounded-lg'}`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span>
                {setPrimaryStatus.type === 'success' ? (
                  <i className="fa-solid fa-check-circle text-green-500 text-sm"></i>
                ) : (
                  <i className="fa-solid fa-exclamation-circle text-red-500 text-sm"></i>
                )}
              </span>
            </div>
            <div className="ml-3">
              <p className={`text-xs ${setPrimaryStatus.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {setPrimaryStatus.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-xs text-gray-500 uppercase tracking-wider">Payment Methods</h3>
        <button 
          className="text-xs font-medium"
          onClick={() => {
            setEditingPayment(null);
            setShowAddPaymentModal(true);
          }}
        >
          <span><i className="fa-solid fa-plus mr-1"></i></span>Add
        </button>
      </div>

      {loadingPaymentMethods ? (
        <div key="mobile-loading" className="py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900 mb-3"></div>
          <p className="text-sm text-gray-600">Loading payment methods...</p>
        </div>
      ) : paymentMethods.length === 0 ? (
        <div key="mobile-empty" className="py-8 text-center bg-white border border-gray-100 rounded-lg">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span><i className="fa-regular fa-credit-card text-gray-400"></i></span>
          </div>
          <p className="text-sm text-gray-600 mb-4">No payment methods saved yet</p>
          <button 
            className="text-xs px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            onClick={() => {
              setEditingPayment(null);
              setShowAddPaymentModal(true);
            }}
          >
            Add First Payment Method
          </button>
        </div>
      ) : (
        <div key="mobile-list" className="space-y-3">
          {paymentMethods.map((payment) => (
            <div key={payment.id} className="bg-white border border-gray-100 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gallery-gray rounded flex items-center justify-center">
                    <span><i className={`${getCardIcon(payment.card?.brand)} text-xl text-gray-700`}></i></span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      {payment.isPrimary && (
                        <span className="inline-block px-2 py-0.5 bg-black text-white text-[9px] uppercase tracking-[0.1em] rounded">Primary</span>
                      )}
                    </div>
                    <p className="text-sm font-medium">
                      {payment.card?.brand ? payment.card.brand.charAt(0).toUpperCase() + payment.card.brand.slice(1) : 'Card'} 
                      {payment.card?.last4 && ` ending in ${payment.card.last4}`}
                    </p>
                    <p className="text-xs text-gray-600">
                      Expires {formatExpirationDate(payment.card?.expMonth, payment.card?.expYear)}
                    </p>
                    {payment.cardholderName && (
                      <p className="text-xs text-gray-600">Cardholder: {payment.cardholderName}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!payment.isPrimary && (
                    <button 
                      className="text-blue-400"
                      onClick={() => handleSetPrimaryPayment(payment.id)}
                    >
                      <i className="fa-solid fa-star text-xs"></i>
                    </button>
                  )}
                  <button 
                    className="text-red-400"
                    onClick={() => handleDeletePayment(payment.id)}
                  >
                    <i className="fa-solid fa-trash text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
</section>

      {/* Notification Preferences */}
      <section className="px-4 md:px-10 py-6 md:py-10 border-t border-gray-100">
        <div className="max-w-4xl">
          {/* Desktop View */}
          <div className="hidden md:block border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-base font-medium mb-1.5">Notification Preferences</h2>
              <p className="text-sm text-gray-600 font-normal">Update and communication preferences</p>
            </div>
            
            {loadingNotifications ? (
              <div className="px-8 py-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mb-4"></div>
                <p className="text-sm text-gray-600">Loading notification preferences...</p>
              </div>
            ) : (
              <div className="px-8 py-8">
                <div className="space-y-10">
                  <div>
                    <h3 className="text-sm font-medium mb-6 tracking-wide">Offers & Orders</h3>
                    <div className="space-y-5">
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">New offer received</p>
                          <p className="text-xs text-gray-600 font-normal">Gallery submits an offer on your behalf</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.new_offer_received.email}
                              onChange={() => handleNotificationChange('offers_orders', 'new_offer_received', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.new_offer_received.in_app}
                              onChange={() => handleNotificationChange('offers_orders', 'new_offer_received', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">In-app</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">Offer status updates</p>
                          <p className="text-xs text-gray-600 font-normal">Offer accepted, declined, or countered</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.offer_status_updates.email}
                              onChange={() => handleNotificationChange('offers_orders', 'offer_status_updates', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.offer_status_updates.in_app}
                              onChange={() => handleNotificationChange('offers_orders', 'offer_status_updates', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 fontNormal">In-app</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">Shipping updates</p>
                          <p className="text-xs text-gray-600 font-normal">Artwork delivery status</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.shipping_updates.email}
                              onChange={() => handleNotificationChange('offers_orders', 'shipping_updates', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.shipping_updates.in_app}
                              onChange={() => handleNotificationChange('offers_orders', 'shipping_updates', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">In-app</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-10 border-t border-gray-100">
                    <h3 className="text-sm font-medium mb-6 tracking-wide">Payments & Invoices</h3>
                    <div className="space-y-5">
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">Payment confirmations</p>
                          <p className="text-xs text-gray-600 font-normal">Confirmation when payments are processed</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.payment_confirmations.email}
                              onChange={() => handleNotificationChange('payments_invoices', 'payment_confirmations', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.payment_confirmations.in_app}
                              onChange={() => handleNotificationChange('payments_invoices', 'payment_confirmations', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">In-app</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">Invoice reminders</p>
                          <p className="text-xs text-gray-600 font-normal">Upcoming and overdue invoices</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.invoice_reminders.email}
                              onChange={() => handleNotificationChange('payments_invoices', 'invoice_reminders', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.invoice_reminders.in_app}
                              onChange={() => handleNotificationChange('payments_invoices', 'invoice_reminders', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">In-app</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-10 border-t border-gray-100">
                    <h3 className="text-sm font-medium mb-6 tracking-wide">Following Artists</h3>
                    <div className="space-y-5">
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">New artwork available</p>
                          <p className="text-xs text-gray-600 font-normal">Artists you follow release new works</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.new_artwork_available.email}
                              onChange={() => handleNotificationChange('following_artists', 'new_artwork_available', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.new_artwork_available.in_app}
                              onChange={() => handleNotificationChange('following_artists', 'new_artwork_available', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">In-app</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-normal mb-1.5 text-gray-700">Price changes</p>
                          <p className="text-xs text-gray-600 font-normal">Significant price adjustments for works by followed artists</p>
                        </div>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.price_changes.email}
                              onChange={() => handleNotificationChange('following_artists', 'price_changes', 'email')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">Email</span>
                          </label>
                          <label className="flex items-center space-x-2.5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.price_changes.in_app}
                              onChange={() => handleNotificationChange('following_artists', 'price_changes', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-xs text-gray-700 font-normal">In-app</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-10 pt-8 border-t border-gray-100 flex justify-end">
                  <button 
                    className={`text-xs px-8 py-3 bg-black text-white hover:bg-gray-800 transition-all duration-200 font-normal tracking-wide ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={async () => {
                      const result = await saveNotificationPreferences();
                      if (result.success) {
                        toastSuccess('Notification preferences updated successfully');
                      }
                    }}
                    disabled={savingNotifications}
                  >
                    {savingNotifications ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-1">Notification Preferences</h3>
            
            {loadingNotifications ? (
              <div className="py-8 text-center bg-white border border-gray-100 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900 mb-3"></div>
                <p className="text-sm text-gray-600">Loading preferences...</p>
              </div>
            ) : (
              <>
                <div className="bg-white border border-gray-100 rounded-lg divide-y divide-gray-100">
                  <div className="p-4">
                    <h4 className="text-sm font-medium mb-3">Offers & Orders</h4>
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">New offer received</p>
                          <p className="text-xs text-gray-600 mt-0.5">When a gallery submits an offer</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.new_offer_received.email}
                              onChange={() => handleNotificationChange('offers_orders', 'new_offer_received', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.new_offer_received.in_app}
                              onChange={() => handleNotificationChange('offers_orders', 'new_offer_received', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-start justify-between pt-3 border-t border-gray-100">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">Offer status updates</p>
                          <p className="text-xs text-gray-600 mt-0.5">Accepted, declined, or countered</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.offer_status_updates.email}
                              onChange={() => handleNotificationChange('offers_orders', 'offer_status_updates', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.offer_status_updates.in_app}
                              onChange={() => handleNotificationChange('offers_orders', 'offer_status_updates', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-start justify-between pt-3 border-t border-gray-100">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">Shipping updates</p>
                          <p className="text-xs text-gray-600 mt-0.5">Track delivery status</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.shipping_updates.email}
                              onChange={() => handleNotificationChange('offers_orders', 'shipping_updates', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.offers_orders.shipping_updates.in_app}
                              onChange={() => handleNotificationChange('offers_orders', 'shipping_updates', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h4 className="text-sm font-medium mb-3">Payments & Invoices</h4>
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">Payment confirmations</p>
                          <p className="text-xs text-gray-600 mt-0.5">When payments are processed</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.payment_confirmations.email}
                              onChange={() => handleNotificationChange('payments_invoices', 'payment_confirmations', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.payment_confirmations.in_app}
                              onChange={() => handleNotificationChange('payments_invoices', 'payment_confirmations', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-start justify-between pt-3 border-t border-gray-100">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">Invoice reminders</p>
                          <p className="text-xs text-gray-600 mt-0.5">Upcoming and overdue invoices</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.invoice_reminders.email}
                              onChange={() => handleNotificationChange('payments_invoices', 'invoice_reminders', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.payments_invoices.invoice_reminders.in_app}
                              onChange={() => handleNotificationChange('payments_invoices', 'invoice_reminders', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h4 className="text-sm font-medium mb-3">Following Artists</h4>
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">New artwork available</p>
                          <p className="text-xs text-gray-600 mt-0.5">When artists you follow release new works</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.new_artwork_available.email}
                              onChange={() => handleNotificationChange('following_artists', 'new_artwork_available', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.new_artwork_available.in_app}
                              onChange={() => handleNotificationChange('following_artists', 'new_artwork_available', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex items-start justify-between pt-3 border-t border-gray-100">
                        <div className="flex-1 pr-4">
                          <p className="text-sm">Price changes</p>
                          <p className="text-xs text-gray-600 mt-0.5">Significant price adjustments for works by followed artists</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.price_changes.email}
                              onChange={() => handleNotificationChange('following_artists', 'price_changes', 'email')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">Email</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              checked={notifications.following_artists.price_changes.in_app}
                              onChange={() => handleNotificationChange('following_artists', 'price_changes', 'in_app')}
                              className="w-4 h-4 border-gray-300 rounded"
                            />
                            <span className="text-xs text-gray-600">App</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button 
                  className={`w-full mt-4 py-3 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 transition-all duration-200 ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={async () => {
                    const result = await saveNotificationPreferences();
                    if (result.success) {
                      toastSuccess('Notification preferences saved');
                    }
                  }}
                  disabled={savingNotifications}
                >
                  {savingNotifications ? 'Saving...' : 'Save Preferences'}
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Legal Documents */}
      <section className="px-4 md:px-10 py-6 md:py-10 border-t border-gray-100">
        <div className="max-w-4xl">
          {/* Desktop View */}
          <div className="hidden md:block border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-base font-medium mb-1.5">Legal & Documentation</h2>
              <p className="text-sm text-gray-600 font-normal">Terms, privacy policy, and legal agreements</p>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="px-8 py-6 flex items-center justify-between hover:bg-gallery-gray transition-all duration-200">
                <div>
                  <h3 className="text-sm font-normal mb-1.5">Terms of Service</h3>
                  <p className="text-xs text-gray-600 font-normal">Last updated: October 2024</p>
                </div>
                <a 
                  href="http://localhost:3000/terms-of-service" 
                  onClick={(e) => handleLinkClick(e, 'http://localhost:3000/terms-of-service')}
                  className="text-xs text-gray-600 hover:text-black underline font-normal tracking-wide"
                  target={isMobile ? undefined : "_blank"}
                  rel={isMobile ? undefined : "noopener noreferrer"}
                >
                  View Document
                </a>
              </div>
              <div className="px-8 py-6 flex items-center justify-between hover:bg-gallery-gray transition-all duration-200">
                <div>
                  <h3 className="text-sm font-normal mb-1.5">Privacy Policy</h3>
                  <p className="text-xs text-gray-600 font-normal">Last updated: October 2024</p>
                </div>
                <a 
                  href="http://localhost:3000/privacy-policy" 
                  onClick={(e) => handleLinkClick(e, 'http://localhost:3000/privacy-policy')}
                  className="text-xs text-gray-600 hover:text-black underline font-normal tracking-wide"
                  target={isMobile ? undefined : "_blank"}
                  rel={isMobile ? undefined : "noopener noreferrer"}
                >
                  View Document
                </a>
              </div>
              <div className="px-8 py-6 flex items-center justify-between hover:bg-gallery-gray transition-all duration-200">
                <div>
                  <h3 className="text-sm font-normal mb-1.5">Cookie Policy</h3>
                  <p className="text-xs text-gray-600 font-normal">Last updated: September 2024</p>
                </div>
                <a 
                  href="#" 
                  className="text-xs text-gray-600 hover:text-black underline font-normal tracking-wide"
                >
                  View Document
                </a>
              </div>
              <div className="px-8 py-6 flex items-center justify-between hover:bg-gallery-gray transition-all duration-200">
                <div>
                  <h3 className="text-sm font-normal mb-1.5">Buyer Agreement</h3>
                  <p className="text-xs text-gray-600 font-normal">Last updated: August 2024</p>
                </div>
                <a 
                  href="#" 
                  className="text-xs text-gray-600 hover:text-black underline font-normal tracking-wide"
                >
                  View Document
                </a>
              </div>
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-1">Legal & Documentation</h3>
            <div className="bg-white border border-gray-100 rounded-lg divide-y divide-gray-100">
              <a 
                href="http://localhost:3000/terms-of-service" 
                className="p-4 flex items-center justify-between active:bg-gray-50"
              >
                <div>
                  <p className="text-sm font-medium">Terms of Service</p>
                  <p className="text-xs text-gray-600 mt-0.5">Last updated: October 2024</p>
                </div>
                <i className="fa-solid fa-chevron-right text-gray-400 text-xs"></i>
              </a>
              <a 
                href="http://localhost:3000/privacy-policy" 
                className="p-4 flex items-center justify-between active:bg-gray-50"
              >
                <div>
                  <p className="text-sm font-medium">Privacy Policy</p>
                  <p className="text-xs text-gray-600 mt-0.5">Last updated: October 2024</p>
                </div>
                <i className="fa-solid fa-chevron-right text-gray-400 text-xs"></i>
              </a>
              <a href="#" className="p-4 flex items-center justify-between active:bg-gray-50">
                <div>
                  <p className="text-sm font-medium">Cookie Policy</p>
                  <p className="text-xs text-gray-600 mt-0.5">Last updated: September 2024</p>
                </div>
                <i className="fa-solid fa-chevron-right text-gray-400 text-xs"></i>
              </a>
              <a href="#" className="p-4 flex items-center justify-between active:bg-gray-50">
                <div>
                  <p className="text-sm font-medium">Buyer Agreement</p>
                  <p className="text-xs text-gray-600 mt-0.5">Last updated: August 2024</p>
                </div>
                <i className="fa-solid fa-chevron-right text-gray-400 text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Render Modals */}
      <EditProfileModal 
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        userProfile={userProfile}
        onSubmit={handleEditProfileSubmit}
        isLoading={editingProfile}
        initialValues={editProfileInitialValues}
        validationSchema={Yup.object({
          firstname: Yup.string().required('First name is required'),
          city: Yup.string(),
          primaryCollectingInterest: Yup.string()
        })}
        phoneCodes={phoneCodes}
      />
      
      <AddAddressModal 
        isOpen={showAddAddressModal}
        onClose={() => {
          setShowAddAddressModal(false);
          setEditingAddress(null);
        }}
        onSubmit={handleAddressSubmit}
        isLoading={isLoading}
        editingAddress={editingAddress}
        addresses={addresses}
        countries={countries}
        phoneCodes={phoneCodes}
      />
      
      <ChangePasswordModal 
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSubmit={handleChangePasswordSubmit}
        isLoading={changingPassword}
      />
      
      <AddPaymentModal
        isOpen={showAddPaymentModal}
        onClose={() => {
          setShowAddPaymentModal(false);
          setEditingPayment(null);
        }}
        onSubmit={handlePaymentSubmit}
        isLoading={addingPayment}
        editingPayment={editingPayment}
      />
      
      <DeleteAccountModal />
      <DeleteAddressConfirmModal />
      <DeletePaymentConfirmModal />
    </div>
  );
};

export default Settings;

